import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { homeFor } from "@/components/ProtectedRoute";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HandCoins, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("method") === "email" ? "email" : "phone");
  const [email, setEmail] = useState("");
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email.trim(), password, mode);
      navigate(homeFor(u));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Invalid credentials";
      setErr(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="borrower-grid min-h-screen flex items-center justify-center px-5 py-12 text-slate-900" data-testid="login-page">
      <div className="w-full max-w-[380px]">
        <section className="bg-white rounded-[28px] border border-white shadow-[0_20px_70px_-25px_rgba(30,90,130,0.25)] px-7 sm:px-8 pt-8 pb-7">
          <div className="flex flex-col items-center mb-7">
            <div className="h-14 w-14 rounded-[18px] bg-[#00A8DE] flex items-center justify-center shadow-lg shadow-sky-500/15"><HandCoins className="h-8 w-8 text-white" strokeWidth={1.8} /></div>
            <h1 className="font-display text-3xl font-bold mt-4" data-testid="login-title">Login</h1>
            <p className="text-xs text-slate-400 mt-1">Login to continue.</p>
          </div>
          <form onSubmit={onSubmit} data-testid="login-form" className="space-y-4">
            <div><Label htmlFor="login-identifier" className="text-xs font-medium text-slate-600">{mode === "phone" ? "Phone Number" : "Email Address"}</Label>
              <Input id="login-identifier" data-testid={mode === "phone" ? "login-phone-input" : "login-email-input"} type={mode === "phone" ? "tel" : "email"} autoComplete="username" required maxLength={mode === "phone" ? 30 : 254} value={email} onChange={(e) => setEmail(e.target.value)} placeholder={mode === "phone" ? "Enter your phone number" : "Enter your email address"} className="mt-1.5 h-12 rounded-2xl bg-slate-50/80 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-sky-400" />
            </div>
            <div><Label htmlFor="login-password" className="text-xs font-medium text-slate-600">Password</Label>
              <div className="relative mt-1.5"><Input id="login-password" data-testid="login-password-input" type={visible ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" className="h-12 rounded-2xl bg-slate-50/80 border-slate-200 text-slate-900 pr-12 placeholder:text-slate-400 focus-visible:ring-sky-400" />
                <button type="button" onClick={() => setVisible((v) => !v)} aria-label={visible ? "Hide password" : "Show password"} aria-pressed={visible} data-testid="show-password-toggle" className="absolute right-1 top-1 h-10 w-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-sky-600 focus-visible:outline focus-visible:outline-sky-500">{visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
              </div>
            </div>
            {err && <div data-testid="login-error" role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-xl p-3">{err}</div>}
            <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full h-12 rounded-full bg-[#00A8DE] hover:bg-[#0096C9] text-white font-semibold shadow-sm transition-colors">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Log In</Button>
          </form>
          <Button asChild variant="outline" className="w-full h-12 mt-3 rounded-full border-slate-200 bg-white hover:bg-sky-50 text-[#009DCD] hover:text-[#008AB5] font-semibold"><Link to="/register" data-testid="login-register-link">Register an Account</Link></Button>
          <button type="button" onClick={() => { setMode((v) => v === "phone" ? "email" : "phone"); setEmail(""); setErr(""); }} data-testid="login-method-toggle" className="block mx-auto text-xs font-medium text-slate-500 hover:text-sky-600 mt-5 transition-colors">{mode === "phone" ? "Admin or email sign in" : "Use phone number instead"}</button>
        </section>
        <p className="mt-6 text-center text-[10px] tracking-wide text-slate-400 flex justify-center gap-1.5 items-center"><ShieldCheck className="h-3 w-3" />LendingPH · Your account, securely connected</p>
      </div>
    </div>
  );
}
